"use client";
import React, { useEffect, useRef, useState } from "react";

export type SortOption = { value: string; label: string };

type SortProps = {
  value: string;
  options: SortOption[];
  onChange: (value: string) => void;
  className?: string;
};

const Sort: React.FC<SortProps> = ({ value, options, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div ref={containerRef} className={`relative inline-block text-left ${className ?? ""}`}>
      {/* Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-4 bg-[#F3EDE9] border border-[#2b2c39] px-3 h-[44px] w-24 text-[#090909] text-sm font-medium"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {/* Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8.66756 4.62479L7.48906 5.8033L5.37672 3.69L5.37609 14.6667H3.70943L3.71005 3.69L1.59649 5.8033L0.417969 4.62479L4.54276 0.5L8.66756 4.62479ZM17.0009 11.3752L12.8761 15.5L8.75131 11.3752L9.92981 10.1967L12.0434 12.31L12.0427 1.33333H13.7094L13.7101 12.31L15.8224 10.1967L17.0009 11.3752Z"
            fill="#090909"
          />
        </svg>
        <span>Sort</span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute mt-1 w-[6rem] bg-white z-20 border-b border-neutral-300">
          {options?.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              role="option"
              aria-selected={value === option.value}
              className="cursor-pointer px-3 py-3 text-sm hover:bg-neutral-200 border-b border-neutral-300 last:border-b-0"
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Sort;
